// Feature flag for Navigator V2 format
// Set to true to use new format from investments.navigator_config_v2
// Set to false to use old format from properties.map_coordinates
export const USE_NAVIGATOR_V2 = true;

// Map configuration constants
export const MAP_CONFIG = {
  // Default SVG viewBox for map coordinates (responsive)
  // Using correct aspect ratio 100:56.5 (1.77:1)
  defaultViewBox: "0 0 100 56.5",
  
  // Responsive viewBox configurations
  responsiveViewBox: {
    mobile: "0 0 100 56.5",
    tablet: "0 0 100 56.5", 
    desktop: "0 0 100 56.5"
  },
  
  // Minimum touch target size for mobile devices (in pixels)
  minTouchTargetSize: 44,
  
  // Animation duration for interactions (in milliseconds)
  animationDuration: 200,
  
  // Color scheme for property status
  colors: {
    available: '#4CAF50',
    reserved: '#FF9800',
    sold: '#F44336',
    coming_soon: '#BDBDBD',
    unavailable: '#9E9E9E',
    selected: '#2196F3',
    hover: '#E3F2FD',
    stroke: '#ffffff'
  },
  
  // Responsive breakpoints
  responsive: {
    mobile: 768,
    tablet: 1024,
    desktop: 1200
  },
  
  // Default image configuration
  defaultImage: {
    url: 'https://kmadenski.github.io/deldom-navigator-mvp/assets/wiz.jpg',
    alt: 'Plan inwestycji',
    aspectRatio: 1.77 // width/height (100/56.5)
  }
};

// Utility function to validate SVG path
export const isValidSVGPath = (path) => {
  if (!path || typeof path !== 'string') return false;
  
  try {
    // Basic validation - check if it looks like an SVG path
    const trimmedPath = path.trim();
    if (trimmedPath.length === 0) return false;
    
    // Check for valid SVG path commands
    const validPathRegex = /^[MmLlHhVvCcSsQqTtAaZz0-9\s,.-]+$/;
    return validPathRegex.test(trimmedPath);
  } catch (error) {
    console.warn('SVG path validation error:', error);
    return false;
  }
};

// Utility function to calculate center point of SVG path bounds
export const calculatePathCenter = (bounds) => {
  if (!bounds || typeof bounds !== 'object') {
    return { x: 50, y: 50 }; // Default center
  }
  
  const { top = 0, right = 100, bottom = 100, left = 0 } = bounds;
  
  return {
    x: left + (right - left) / 2,
    y: top + (bottom - top) / 2
  };
};

// Utility function to generate sample property coordinates for testing
export const generateSampleCoordinates = (propertyId, index, total) => {
  // Generate simple rectangular regions for testing with correct viewBox (100x56.5)
  const gridCols = Math.ceil(Math.sqrt(total));
  const gridRows = Math.ceil(total / gridCols);
  
  const col = index % gridCols;
  const row = Math.floor(index / gridCols);
  
  const cellWidth = 80 / gridCols; // 80 units out of 100 total width
  const cellHeight = 46.5 / gridRows; // 46.5 units out of 56.5 total height
  
  const x = 10 + col * cellWidth; // Start at 10 units from left
  const y = 5 + row * cellHeight; // Start at 5 units from top
  const width = cellWidth * 0.8;
  const height = cellHeight * 0.8;
  
  // Create a simple rectangular path
  const svgPath = `M ${x} ${y} L ${x + width} ${y} L ${x + width} ${y + height} L ${x} ${y + height} Z`;
  
  const bounds = {
    top: y,
    right: x + width,
    bottom: y + height,
    left: x
  };
  
  return {
    svgPath,
    center: calculatePathCenter(bounds),
    bounds
  };
};

// Utility function to validate property coordinate data
export const validatePropertyCoordinates = (property) => {
  if (!property || !property.mapCoordinates) {
    return false;
  }
  
  const { mapCoordinates } = property;
  
  // Check required fields
  if (!mapCoordinates.svgPath) {
    return false;
  }
  
  // Validate SVG path
  if (!isValidSVGPath(mapCoordinates.svgPath)) {
    return false;
  }
  
  // Validate center coordinates (optional but recommended)
  if (mapCoordinates.center) {
    const { x, y } = mapCoordinates.center;
    if (typeof x !== 'number' || typeof y !== 'number' || isNaN(x) || isNaN(y)) {
      console.warn(`Invalid center coordinates for property ${property.id}`);
    }
  }
  
  return true;
};

// Utility function to get status color
export const getPropertyStatusColor = (statusCode) => {
  return MAP_CONFIG.colors[statusCode] || MAP_CONFIG.colors.unavailable;
};

// Utility function to check if device supports touch
export const isTouchDevice = () => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

// Utility function to get responsive map size
export const getResponsiveMapSize = (containerWidth) => {
  if (containerWidth <= MAP_CONFIG.responsive.mobile) {
    return 'mobile';
  } else if (containerWidth <= MAP_CONFIG.responsive.tablet) {
    return 'tablet';
  }
  return 'desktop';
};

// Utility function to get responsive viewBox
export const getResponsiveViewBox = (containerWidth) => {
  const size = getResponsiveMapSize(containerWidth);
  return MAP_CONFIG.responsiveViewBox[size] || MAP_CONFIG.defaultViewBox;
};

// Utility function to scale SVG coordinates for responsive design
export const scaleCoordinatesForViewBox = (coordinates, sourceViewBox, targetViewBox) => {
  if (!coordinates || !sourceViewBox || !targetViewBox) {
    return coordinates;
  }
  
  // Parse viewBox values ("x y width height")
  const parseViewBox = (vb) => {
    const [x, y, width, height] = vb.split(' ').map(Number);
    return { x, y, width, height };
  };
  
  const source = parseViewBox(sourceViewBox);
  const target = parseViewBox(targetViewBox);
  
  // Calculate scaling factors
  const scaleX = target.width / source.width;
  const scaleY = target.height / source.height;
  
  // Scale SVG path coordinates with proper coordinate parsing
  if (coordinates.svgPath) {
    let scaledPath = coordinates.svgPath;
    
    // Parse SVG path commands and scale coordinates properly
    scaledPath = scaledPath.replace(/([MLHVCSQTAZ])\s*([^MLHVCSQTAZ]*)/gi, (match, command, coords) => {
      if (!coords.trim()) return command;
      
      // Split coordinates by space or comma
      const numbers = coords.trim().split(/[\s,]+/).filter(n => n && !isNaN(parseFloat(n)));
      
      // Scale coordinates based on command type
      const scaledNumbers = numbers.map((num, index) => {
        const numVal = parseFloat(num);
        // For most commands, even indices are X coordinates, odd are Y coordinates
        // This is a simplified approach - for production, use a proper SVG path parser
        if (command.toLowerCase() === 'h') {
          return (numVal * scaleX).toFixed(2);
        } else if (command.toLowerCase() === 'v') {
          return (numVal * scaleY).toFixed(2);
        } else {
          return index % 2 === 0 ? (numVal * scaleX).toFixed(2) : (numVal * scaleY).toFixed(2);
        }
      });
      
      return command + ' ' + scaledNumbers.join(' ');
    });
    
    return {
      ...coordinates,
      svgPath: scaledPath,
      center: coordinates.center ? {
        x: coordinates.center.x * scaleX,
        y: coordinates.center.y * scaleY
      } : coordinates.center,
      bounds: coordinates.bounds ? {
        top: coordinates.bounds.top * scaleY,
        right: coordinates.bounds.right * scaleX,
        bottom: coordinates.bounds.bottom * scaleY,
        left: coordinates.bounds.left * scaleX
      } : coordinates.bounds
    };
  }
  
  return coordinates;
};

// Utility function to detect if coordinates are in legacy format
export const isLegacyFormat = (coordinates) => {
  if (!coordinates || !coordinates.svgPath) return false;
  
  // Extract all numbers from SVG path
  const numbers = coordinates.svgPath.match(/[\d.]+/g);
  if (!numbers || numbers.length === 0) return false;
  
  // Check if any coordinate is > 100 (indicating old 1000x800 format)
  const maxValue = Math.max(...numbers.map(n => parseFloat(n)));
  return maxValue > 100;
};

// Utility function to convert coordinates from old viewBox to actual image dimensions
export const convertLegacyCoordinates = (coordinates) => {
  if (!coordinates) return coordinates;

  // For antoninow-park coordinates, they are already in the correct scale (1300x645)
  // Just return them as-is since we're now using the proper viewBox
  return coordinates;
};

export default MAP_CONFIG;